/**
 * Per-league Simulation tab — Game Data → EPL/WC → Simulation.
 *
 * Renders one card per real-world gameweek/scope (GW1..GW38 for EPL,
 * MD1/MD2/MD3/R32/R16/QF/SF/F for WC) with fixture-derived state. Does
 * NOT show competitions — competition lifecycle is per-Competition
 * bookkeeping and was previously surfaced here, which made E2E + admin
 * comps clutter the view. The state of "GW33" in the world is a
 * function of fixture state + the virtual clock, nothing else.
 *
 *   EPL: replays SportsMonks snapshots from past gameweeks. Pressing
 *        Advance walks the virtual clock forward; the match-schedule
 *        loop replays real player stats from `SimulationData` for any
 *        EPL fixture whose kickoff has crossed the new clock value.
 *
 *   WC:  no real data exists yet. The match-schedule loop calls
 *        `produceWCStatsForFixture` (Math.random) for any WC fixture
 *        going from `live` → `completed`. Each Advance produces a
 *        fresh, non-reproducible result. WC also exposes a "Reset WC
 *        sim" action to wipe generated stats and rewind the clock.
 *
 * Production gating: clock controls + reset actions are hidden behind
 * an "isProduction" check — round status + fixture lists are still
 * visible (operationally useful when WC2026 is live and SportsMonks
 * is filling in stats in real time).
 */

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { isProduction } from '../system/api';
import { formatTimestamp } from '../system/shared';

import {
  type LeagueName,
  type ScopeFixtureStatus,
  type SimulationScopeRow,
  useSimulationRoundStatus,
  useResetWCSimulation,
} from './api';
import { SimulationControls } from './SimulationControls';

export function SimulationTab({ league }: { league: LeagueName }) {
  const status = useSimulationRoundStatus(league);
  const rounds = status.data?.data ?? [];

  return (
    <div className="space-y-6">
      <StatusBanner league={league} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {league === 'WC' ? 'Tournament rounds' : 'Season rounds'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading round status…</div>
          ) : status.error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {status.error instanceof Error ? status.error.message : 'Failed to load rounds.'}
            </div>
          ) : rounds.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
              No {league} fixtures exist yet — nothing to simulate.{' '}
              {league === 'EPL'
                ? 'Run `npm run seed` to load the EPL season.'
                : 'Run `wc-sync-all` to load the WC tournament.'}
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {rounds.map((r) => (
                <RoundCard key={r.scope_code} round={r} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isProduction() ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Clock controls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-900">
              <div className="font-medium">Disabled in production.</div>
              <div className="mt-1 text-amber-800/80">
                Clock controls are gated off the live API to prevent accidental
                nudges to real fixture timing. Round + fixture status above
                stays visible because it reflects real ingestion state.
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Clock controls</CardTitle>
          </CardHeader>
          <CardContent>
            <SimulationControls />
          </CardContent>
        </Card>
      )}

      {league === 'WC' && !isProduction() ? <WCResetCard /> : null}
    </div>
  );
}

function StatusBanner({ league }: { league: LeagueName }) {
  if (league === 'EPL') {
    return (
      <div className="rounded-md border bg-card px-4 py-3 text-sm">
        <div className="font-medium">EPL 2025/26 — replay simulation</div>
        <div className="mt-1 text-xs text-muted-foreground">
          Sim mode replays SportsMonks snapshots from past gameweeks. Pressing
          Advance walks the virtual clock forward and the match-schedule loop
          fills in real stats for fixtures whose kickoff has passed.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card px-4 py-3 text-sm">
      <div className="font-medium">FIFA World Cup 2026 — randomised simulation</div>
      <div className="mt-1 text-xs text-muted-foreground">
        Tournament hasn&apos;t happened yet, so each Advance generates fresh
        random outcomes for any WC fixture transitioning to live. Reset the WC
        sim below to wipe generated stats and replay from scratch — every run
        produces a different scoreline.
      </div>
    </div>
  );
}

function RoundCard({ round }: { round: SimulationScopeRow }) {
  const tone = scopeStatusTone(round.status);
  const showLive = round.fixture_live > 0;
  const showProgress = round.fixture_resolved > 0 && round.fixture_resolved < round.fixture_total;

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-medium">{round.label}</div>
        <span className={cn('rounded-full px-2 py-0.5 text-[10px] uppercase', tone)}>
          {round.status}
        </span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {round.fixture_total} {round.fixture_total === 1 ? 'fixture' : 'fixtures'}
        {showLive ? ` · ${round.fixture_live} live` : null}
        {showProgress ? ` · ${round.fixture_resolved}/${round.fixture_total} done` : null}
      </div>
      {round.earliest_kickoff ? (
        <div className="mt-1 text-[11px] text-muted-foreground">
          {round.status === 'completed'
            ? `Final kickoff ${formatTimestamp(round.latest_kickoff ?? round.earliest_kickoff)}`
            : `First kickoff ${formatTimestamp(round.earliest_kickoff)}`}
        </div>
      ) : null}
    </div>
  );
}

function scopeStatusTone(status: ScopeFixtureStatus): string {
  switch (status) {
    case 'scheduled':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
    case 'live':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
    case 'completed':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function WCResetCard() {
  const reset = useResetWCSimulation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  async function handleReset() {
    setActionError(null);
    setLastResult(null);
    if (
      !window.confirm(
        'Reset the WC simulation? This wipes all generated WC stats, returns fixtures to scheduled, and re-opens rounds. EPL data is untouched.',
      )
    ) {
      return;
    }
    try {
      const res = await reset.mutateAsync();
      const w = res.wiped;
      setLastResult(
        `Wiped ${w.player_stats} stats, ${w.results} results, ` +
          `reset ${w.fixtures_reset} fixtures, reopened ${w.rounds_reopened} rounds. ` +
          (res.clock_anchor_iso
            ? `Suggested clock anchor: ${res.clock_anchor_iso} (use Set time above).`
            : ''),
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Reset failed');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Reset WC simulation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Wipes generated WC PlayerStat + Result rows, returns WC fixtures to
          scheduled, reopens WC rounds + competitions, and uneliminates any
          knocked-out players. EPL state is untouched. After resetting, use
          the clock controls above to anchor the clock and Advance through the
          tournament again.
        </p>
        <div>
          <Button
            size="sm"
            variant="destructive"
            disabled={reset.isPending}
            onClick={handleReset}
          >
            Reset WC sim
          </Button>
        </div>
        {actionError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {actionError}
          </div>
        ) : null}
        {lastResult ? (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            {lastResult}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
